import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ProfileProvider, useProfileStats } from './context/ProfileContext';
import { StripeProvider } from './context/StripeContext';
import { AdminProvider } from './context/AdminContext';
import { TrainerProvider } from './context/TrainerContext';
import AccessibilityEnhancer from './components/AccessibilityFixes/AccessibilityEnhancer';
import Toaster from './components/Toaster/Toaster';
import Lottie from 'lottie-react';
import loadingAnimation from './components/Animations/loading.json';
import NotFound from './components/NotFound/NotFound';
import CookieBanner from './components/CookieBanner/CookieBanner';
import ChatBotToggle from './components/ChatBot/ChatBotToggle';
import AuthCallback from './components/Auth/AuthCallback';
import ConsoleErrorSuppressor from './components/ErrorSuppression/ConsoleErrorSuppressor';
import activityTracker from './utils/activityTracker.jsx';


// CRITICAL: Ultra-aggressive lazy loading - split every possible component
const AccountSetup = React.lazy(() => 
  import('./components/Auth/AccountSetup').then(module => ({ default: module.default }))
);

// Core pages - still lazy but higher priority
const HomePage = React.lazy(() => 
  import('./pages/Home/HomePage').then(module => ({ default: module.default }))
);

// Workout components - most memory intensive, split aggressively
const WorkoutPlan = React.lazy(() => 
  import('./pages/WorkoutPlan/WorkoutPlan').then(module => ({ default: module.default }))
);

// const RealTimeTracking = React.lazy(() => 
//   import('./pages/WorkoutPage/RealTimeTracking1').then(module => ({ default: module.default }))
// );

const RealTimeTracking2 = React.lazy(() => 
  import('./pages/WorkoutPage/RealTimeTracking2').then(module => ({ default: module.default }))
);

// const GamifiedRealTimeTracking = React.lazy(() => 
//   import('./pages/GamifiedWorkout/GamifiedRealTimeTracking').then(module => ({ default: module.default }))
// );

const GamifiedWorkoutPage = React.lazy(() => 
  import('./pages/WorkoutPage/GamifiedWorkoutPage').then(module => ({ default: module.default }))
);

// Diet plan components - chunk separately
const DietPlan = React.lazy(() => 
  import('./pages/DietPlan/DietPlan').then(module => ({ default: module.default }))
);

const PersonalizedDietPlan = React.lazy(() => 
  import('./pages/PersonalizedForm/PersonalizedDietPlan').then(module => ({ default: module.default }))
);

// Heavy AI/ML components - load only when needed
// const AITrainerView = React.lazy(() => 
//   import('./components/AITrainer/AITrainerView').then(module => ({ default: module.default }))
// );

// Separate chunk for all workout plan types
const BeginnerWorkoutPlan = React.lazy(() => 
  import('./pages/WorkoutPlan/BeginnerWorkoutPlan').then(module => ({ default: module.default }))
);
const IntermediateWorkoutPlan = React.lazy(() => 
  import('./pages/WorkoutPlan/IntermediateWorkoutPlan').then(module => ({ default: module.default }))
);
const AdvancedWorkoutPlan = React.lazy(() => 
  import('./pages/WorkoutPlan/AdvancedWorkoutPlan').then(module => ({ default: module.default }))
);

// Diet plan variations - separate chunks
const CuttingDietPlan = React.lazy(() => 
  import('./pages/DietPlan/CuttingDietPlan').then(module => ({ default: module.default }))
);
const EPDietPlan = React.lazy(() => 
  import('./pages/DietPlan/EPDietPlan').then(module => ({ default: module.default }))
);
const KetoDietPlan = React.lazy(() => 
  import('./pages/DietPlan/KetoDietPlan').then(module => ({ default: module.default }))
);
const IntermittentDietPlan = React.lazy(() => 
  import('./pages/DietPlan/IntermittentDietPlan').then(module => ({ default: module.default }))
);
const VeganDietPlan = React.lazy(() => 
  import('./pages/DietPlan/VeganDietPlan').then(module => ({ default: module.default }))
);
const PaleoDietPlan = React.lazy(() => 
  import('./pages/DietPlan/PaleoDietPlan').then(module => ({ default: module.default }))
);

// Weight management plans
const WeigthLossDietPlan = React.lazy(() => 
  import('./pages/WeightLoss/WeigthLossDietPlan').then(module => ({ default: module.default }))
);
const MuscleBuildingDietPlan = React.lazy(() => 
  import('./pages/MuscleBulding/MuscleBuildingDietPlan').then(module => ({ default: module.default }))
);

// Personalized form
const PersonalizedWorkoutPlan = React.lazy(() => 
  import('./pages/PersonalizedForm/PersonalizedWorkoutPlan').then(module => ({ default: module.default }))
);

// Landing and tracking
const RealTimeTrackingLanding = React.lazy(() => 
  import('./pages/RealTimeTracking/RealTimeTrackingLanding').then(module => ({ default: module.default }))
);

// Blog components - low priority
const ConqueringConsistency = React.lazy(() => 
  import('./pages/Blogs/ConqueringConsistency').then(module => ({ default: module.default }))
);
const WeightLossBlog = React.lazy(() => 
  import('./pages/Blogs/WeightLossBlog').then(module => ({ default: module.default }))
);
const DietBlog = React.lazy(() => 
  import('./pages/Blogs/DietBlog').then(module => ({ default: module.default }))
);
const NutritionBlog = React.lazy(() => 
  import('./pages/Blogs/NutritionBlog').then(module => ({ default: module.default }))
);

// Gym and blog sections
// const GymsSection = React.lazy(() => 
//   import('./components/GymSection/GymSection').then(module => ({ default: module.default }))
// );
const BlogSection = React.lazy(() => 
  import('./components/Blog/Blogs').then(module => ({ default: module.default }))
);
const GymPage = React.lazy(() => 
  import('./components/GymSection/GymPage').then(module => ({ default: module.default }))
);
const ServicesPage = React.lazy(() => 
  import('./pages/Services/ServicesPage').then(module => ({ default: module.default }))
);

// Payment and subscription
const Subscription = React.lazy(() => 
  import('./components/Subscription/Subscription').then(module => ({ default: module.default }))
);
const PaymentSuccess = React.lazy(() => 
  import('./components/Subscription/PaymentSuccess').then(module => ({ default: module.default }))
);

// Utility pages
const BlogsList = React.lazy(() => 
  import('./components/Blog/BlogsList').then(module => ({ default: module.default }))
);
const CookiePolicy = React.lazy(() => 
  import('./components/CookiePolicy/CookiePolicy').then(module => ({ default: module.default }))
);
const UserDetailsForm = React.lazy(() => 
  import('./components/UserDetails/UserDetailsForm').then(module => ({ default: module.default }))
);

// AI Trainer components
// const AITrainerHistory = React.lazy(() => 
//   import('./components/AITrainerHistory/AITrainerHistory').then(module => ({ default: module.default }))
// );
// const AITrainerHistoryDetail = React.lazy(() => 
//   import('./components/AITrainerHistory/AITrainerHistoryDetail').then(module => ({ default: module.default }))
// );

// Cookie help
const StripeCookieHelp = React.lazy(() => 
  import('./pages/CookieHelp/StripeCookieHelp').then(module => ({ default: module.default }))
);

// Profile components - separate chunk
const ProfileLayout = React.lazy(() => 
  import('./pages/Profile/Profile').then(module => ({ default: module.default }))
);
const ProfileSection = React.lazy(() => 
  import('./components/ProfileSection/ProfileSection').then(module => ({ default: module.default }))
);
const BookMarks = React.lazy(() => 
  import('./components/BookMarks/BookMarks').then(module => ({ default: module.default }))
);
const SupportAndFeedback = React.lazy(() => 
  import('./components/Support/Support').then(module => ({ default: module.default }))
);
const TermsNCondition = React.lazy(() => 
  import('./components/TermsNConditon/TermsNCondition').then(module => ({ default: module.default }))
);
const PrivacyPolicy = React.lazy(() => 
  import('./components/Privacy/PrivacyPolicy').then(module => ({ default: module.default }))
);

// History components
const WorkoutPlansHistory = React.lazy(() => 
  import('./components/WorkoutPlansHistory/WorkoutPlansHistory').then(module => ({ default: module.default }))
);
const WorkoutPlanResult = React.lazy(() => 
  import('./components/Form/WorkoutPlanResult').then(module => ({ default: module.default }))
);
const DietPlanHistory = React.lazy(() => 
  import('./components/DietPlanHistory/DietPlanHistory').then(module => ({ default: module.default }))
);
const DietPlanResultSection = React.lazy(() => 
  import('./components/PersonalizedForm/DietPlanResultSection').then(module => ({ default: module.default }))
);
const ExerciseHistory = React.lazy(() => 
  import('./components/ExerciseHistory/ExerciseHistory').then(module => ({ default: module.default }))
);

// Plans components
const MyPlans = React.lazy(() => 
  import('./components/MyPlans/MyPlans').then(module => ({ default: module.default }))
);
const PlanDetails = React.lazy(() => 
  import('./components/MyPlans/PlanDetails').then(module => ({ default: module.default }))
);

// Admin components - separate chunk for admin functionality
const AdminLayout = React.lazy(() => 
  import('./admin/components/AdminLayout/AdminLayout').then(module => ({ default: module.default }))
);
const AdminDashboard = React.lazy(() => 
  import('./admin/components/AdminDashboard/AdminDashboard').then(module => ({ default: module.default }))
);
const AdminUsersWithTabs = React.lazy(() => 
  import('./admin/components/AdminUsers/AdminUsersWithTabs').then(module => ({ default: module.default }))
);
const AdminAnalytics = React.lazy(() => 
  import('./admin/components/AdminAnalytics/AdminAnalytics').then(module => ({ default: module.default }))
);
const AdminActivityDashboard = React.lazy(() => 
  import('./admin/components/ActivityTracker/AdminActivityDashboard').then(module => ({ default: module.default }))
);
const ActivityDemoPage = React.lazy(() => 
  import('./pages/ActivityTracker/ActivityDemoPage').then(module => ({ default: module.default }))
);
const ActivityTrackerPage = React.lazy(() => 
  import('./pages/ActivityTracker/ActivityTrackerPage').then(module => ({ default: module.default }))
);
const AdminSubscriptionManagement = React.lazy(() => 
  import('./admin/components/AdminSubscriptionManagement/AdminSubscriptionManagement').then(module => ({ default: module.default }))
);
const AdminSettings = React.lazy(() => 
  import('./admin/components/AdminSettings/AdminSettings').then(module => ({ default: module.default }))
);
const AdminRatingsAndFeedback = React.lazy(() => 
  import('./admin/components/AdminRatingsAndFeedback/AdminRatingsAndFeedback').then(module => ({ default: module.default }))
);
const AdminContactForms = React.lazy(() => 
  import('./admin/components/AdminContactForms/AdminContactForms').then(module => ({ default: module.default }))
);

// Sub-Admin components - separate chunk for sub-admin functionality
const SubAdminLayout = React.lazy(() => 
  import('./subadmin/components/SubAdminLayout/SubAdminLayout').then(module => ({ default: module.default }))
);
const SubAdminDashboard = React.lazy(() => 
  import('./subadmin/components/SubAdminDashboard/SubAdminDashboard').then(module => ({ default: module.default }))
);
const SubAdminTrainerManagement = React.lazy(() => 
  import('./subadmin/pages/TrainerManagement').then(module => ({ default: module.default }))
);

// Trainer components
const TrainerLayout = React.lazy(() => 
  import('./trainer/components/TrainerLayout/TrainerLayout').then(module => ({ default: module.default }))
);
const TrainerDashboard = React.lazy(() => 
  import('./trainer/components/TrainerDashboard/TrainerDashboard').then(module => ({ default: module.default }))
);
const TrainerMembers = React.lazy(() => 
  import('./trainer/pages/TrainerMembers').then(module => ({ default: module.default }))
);
const MemberDetails = React.lazy(() => 
  import('./trainer/pages/MemberDetails').then(module => ({ default: module.default }))
);
const MemberProgress = React.lazy(() => 
  import('./trainer/pages/MemberProgress').then(module => ({ default: module.default }))
);
const MemberSessions = React.lazy(() => 
  import('./trainer/pages/MemberSessions').then(module => ({ default: module.default }))
);
const TrainerAnalytics = React.lazy(() => 
  import('./trainer/pages/TrainerAnalytics').then(module => ({ default: module.default }))
);
const GenerateWorkoutPlan = React.lazy(() => 
  import('./trainer/pages/GenerateWorkoutPlan').then(module => ({ default: module.default }))
);
const GenerateDietPlan = React.lazy(() => 
  import('./trainer/pages/GenerateDietPlan').then(module => ({ default: module.default }))
);

const ViewPlans = React.lazy(() => 
  import('./trainer/pages/ViewPlans').then(module => ({ default: module.default }))
);

const EditWorkoutPlan = React.lazy(() => 
  import('./trainer/pages/EditWorkoutPlan').then(module => ({ default: module.default }))
);

const EditDietPlan = React.lazy(() => 
  import('./trainer/pages/EditDietPlan').then(module => ({ default: module.default }))
);

// Admin Trainer Management
const AdminTrainerManagement = React.lazy(() => 
  import('./admin/pages/TrainerManagement').then(module => ({ default: module.default }))
);


// Component to conditionally render ChatBotToggle based on current route
// Optimized Lottie loading component
const Loading = React.memo(() => (
  <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
    <div className="text-center">
      <Lottie 
        animationData={loadingAnimation} 
        style={{ width: 250, height: 250 }}
        loop={true}
        autoplay={true}
      />
      <p className="text-white mt-4 text-lg font-medium">Loading...</p>
    </div>
  </div>
));

const ConditionalChatBot = () => {
  const location = useLocation();
  
  // Don't show ChatBot on workout pages and user profile routes
  if (
    location.pathname === '/workout-page' || 
    location.pathname === '/gamified-workout-play' || 
    location.pathname.startsWith('/user/')
  ) {
    return null;
  }
  
  return <ChatBotToggle />;
};

// Global scroll reset on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const el =
      document.querySelector('[data-scroll-container]') ||
      document.scrollingElement ||
      document.documentElement;

    // Always reset to top first
    el.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    // If navigating to a hash, scroll to it after layout settles
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }
  }, [pathname, hash]);

  return null;
};

// Component to track page views automatically
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view when location changes
    const pageName = location.pathname || 'Unknown Page';
    activityTracker.trackPageView(pageName);
  }, [location.pathname]);
  
  return null;
};

// ProfileRoutes component to handle profile-related routes
const ProfileRoutes = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { fetchUserProfile, fetchProfileStats } = useProfileStats();
  
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        await fetchUserProfile();
        await fetchProfileStats();
      } catch (error) {
        console.error("Error loading profile data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfileData();
  }, []);
  
  if (isLoading) {
    return <Loading />;
  }
  
  return (
    <Suspense fallback={<Loading />}>
      <Toaster/>
      <Routes>
        <Route path="/" element={<ProfileLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfileSection />} />
          <Route path="bookmarks" element={<BookMarks />} />
          <Route path="support" element={<SupportAndFeedback />} />
          <Route path="terms" element={<TermsNCondition />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="workout-history" element={<WorkoutPlansHistory />} />
          <Route path="workout-plan/:plan_id" element={<WorkoutPlanResult />} />
          <Route path="diet-history" element={<DietPlanHistory />} />
          <Route path="diet-plan/:plan_id" element={<DietPlanResultSection />} />
          <Route path="exercise-history" element={<ExerciseHistory />} />
          <Route path="my-plans" element={<MyPlans />} />
          <Route path=":type-plan/:planId/progress" element={<PlanDetails />} />
          <Route path="/my-plans/:planType/:planId" element={<PlanDetails />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

// AdminRoutes component to handle admin-related routes
const AdminRoutes = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setIsAdmin(userData.user_type === "admin");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <AdminProvider>
      <Toaster/>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<AdminLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersWithTabs />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="daily-active-users" element={<AdminActivityDashboard />} />
            <Route path="activity-demo" element={<ActivityDemoPage />} />
            <Route path="subscription-management" element={<AdminSubscriptionManagement />} />
            <Route path="trainers" element={<AdminTrainerManagement />} />
            <Route path="ratings-feedback" element={<AdminRatingsAndFeedback />} />
            <Route path="contact-forms" element={<AdminContactForms />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
        </Routes>
      </Suspense>
    </AdminProvider>
  );
};

// SubAdminRoutes component to handle sub-admin-related routes
const SubAdminRoutes = () => {
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setIsSubAdmin(userData.user_type === "sub_admin");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  // Redirect non-sub-admin users
  if (!isSubAdmin) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <AdminProvider>
      <Toaster/>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<SubAdminLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<SubAdminDashboard />} />
            <Route path="users" element={<AdminUsersWithTabs />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="daily-active-users" element={<AdminActivityDashboard />} />
            <Route path="activity-demo" element={<ActivityDemoPage />} />
            <Route path="subscription-management" element={<AdminSubscriptionManagement />} />
            <Route path="trainers" element={<SubAdminTrainerManagement />} />
            <Route path="contact-forms" element={<AdminContactForms />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
        </Routes>
      </Suspense>
    </AdminProvider>
  );
};

// TrainerRoutes component to handle trainer-related routes
const TrainerRoutes = () => {
  const [isTrainer, setIsTrainer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setIsTrainer(userData.user_type === "trainer");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  // Redirect non-trainer users
  if (!isTrainer) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <TrainerProvider>
      <Toaster/>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<TrainerLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<TrainerDashboard />} />
            <Route path="members" element={<TrainerMembers />} />
            <Route path="members/:memberId" element={<MemberDetails />} />
            <Route path="members/:memberId/progress" element={<MemberProgress />} />
            <Route path="members/:memberId/sessions" element={<MemberSessions />} />
            <Route path="analytics" element={<TrainerAnalytics />} />
            <Route path="generate-workout-plan" element={<GenerateWorkoutPlan />} />
            <Route path="generate-diet-plan" element={<GenerateDietPlan />} />
            <Route path="view-plans" element={<ViewPlans />} />
            <Route path="edit-workout-plan/:planId" element={<EditWorkoutPlan />} />
            <Route path="edit-diet-plan/:planId" element={<EditDietPlan />} />
          </Route>
        </Routes>
      </Suspense>
    </TrainerProvider>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Listen for user state changes from localStorage and custom events
  useEffect(() => {
    // Check initial authentication state
    const checkAuthState = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          JSON.parse(storedUser); // Validate JSON without storing
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Error parsing user data:", error);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    };
    
    // Initial check
    checkAuthState();
    
    // Listen for localStorage and custom storage events from Header component
    window.addEventListener('storage', checkAuthState);
    
    return () => {
      window.removeEventListener('storage', checkAuthState);
    };
  }, []);

  return (
    <ProfileProvider>
      <StripeProvider>
        <div className='bg-black'>
          <BrowserRouter>
            <AppContent 
              isAuthenticated={isAuthenticated}
            />
          </BrowserRouter>
        </div>
      </StripeProvider>
    </ProfileProvider>
  );
}

// Separate component to use navigation context
const AppContent = ({ isAuthenticated }) => (
  <>
    <AccessibilityEnhancer />
   <ConsoleErrorSuppressor /> 
    <ScrollToTop />
    <Suspense fallback={<Loading />}>
     <Toaster />
      <Routes>
        <Route path="/" element={<HomePage/>} key={"home"} />
        <Route path="/workout-plan" element={<WorkoutPlan/>} key={"workout-plan"}/>
        <Route path="/diet-plan" element={<DietPlan/>} key={"diet-plan"}/>
        <Route path="/personalized-diet-plan" element={<PersonalizedDietPlan/>} key={"personalized-diet-plan"}/>
        <Route path="/weight-loss-diet-plan" element={<WeigthLossDietPlan/>} key={"weight-loss-diet-plan"}/>
        <Route path="/muscle-building-diet-plan" element={<MuscleBuildingDietPlan/>} key={"muscle-building-diet-plan"}/>
        <Route path="/beginner-workout-plan" element={<BeginnerWorkoutPlan/>} key={"beginner-workout-plan"}/>
        <Route path="/cutting-diet-plan" element={<CuttingDietPlan/>} key={"cutting-diet-plan"}/>   
        <Route path="/intermediate-workout-plan" element={<IntermediateWorkoutPlan/>} key={"intermediate-workout-plan"}/>
        <Route path="/advanced-workout-plan" element={<AdvancedWorkoutPlan/>} key={"advanced-workout-plan"}/>
        <Route path="/personalized-workout-plan-form" element={<PersonalizedWorkoutPlan/>} key={"personalized-workout-plan-form"}/>
        <Route path="/endurance-performance-diet-plans" element={<EPDietPlan/>} key={"endurance-performance-diet-plans"}/>
        <Route path="/keto-diet-plan" element={<KetoDietPlan/>} key={"keto-diet-plan"}/>
        <Route path="/intermittent-diet-plan" element={<IntermittentDietPlan/>} key={"intermittent-diet-plan"}  />
        <Route path="/vegan-diet-plan" element={<VeganDietPlan/>} key={"vegan-diet-plan"}/>
        <Route path="/paleo-diet-plan" element={<PaleoDietPlan/>} key={"paleo-diet-plan"}/>
        {/* <Route path="/workout-page-old" element={<RealTimeTracking/>} key={"workout-page"}/> */}
        <Route path="/workout-page" element={<RealTimeTracking2/>} key={"workout-page-new"}/>
        {/* <Route path="/gamified-workout-page" element={<GamifiedRealTimeTracking/>} key={"gamified-workout-page"}/> // from old real time tracking to game*/} 
        <Route path="/gamified-workout-play" element={<GamifiedWorkoutPage/>} key={"gamified-workout-play"}/>
        <Route path="/real-time-workout" element={<RealTimeTrackingLanding/>} key={"real-time-workout"}/>
        <Route path="/conquering-consistency" element={<ConqueringConsistency/>} key={"conquering-consistency"}/>
        <Route path="/weight-loss-blog" element={<WeightLossBlog/>} key={"weight-loss-blog"}/>
        <Route path="/gyms" element={<GymPage/>} key={"gyms"}/>
        <Route path="/services" element={<ServicesPage/>} key={"services"}/>
        <Route path="/blogs" element={<BlogSection/>} key={"blogs"}/>
        <Route path="/diet-blog" element={<DietBlog/>} key={"diet-blog"}/>
        <Route path="/privacy-policy" element={<PrivacyPolicy/>} key={"privacy-policy"}/>
        <Route path="/terms-and-conditions" element={<TermsNCondition/>} key={"terms-and-conditions"}/>
        <Route path="/support" element={<SupportAndFeedback/>} key={"support"}/>
        <Route path="/cookie-policy" element={<CookiePolicy/>} key={"cookie-policy"}/>
        {/* <Route path="/ai-trainer" element={<AITrainerView/>} key={"ai-trainer"}/> */}
        {/* <Route path="/ai-trainer-history" element={<AITrainerHistory/>} key={"ai-trainer-history"}/> */}
        {/* <Route path="/ai-trainer-history/:planId" element={<AITrainerHistoryDetail/>} key={"ai-trainer-history-detail"}/> */}
        <Route path="/user-details" element={<UserDetailsForm/>} key={"user-details"}/>
        {isAuthenticated && (
          <Route path="/user/*" element={<ProfileRoutes />} key={"user"} />
        )}
        {isAuthenticated && (
          <Route path="/activity-tracker" element={<ActivityTrackerPage />} key={"activity-tracker"} />
        )}
        {/* Admin Routes - protected by authentication */}
        <Route path="/admin/*" element={<AdminRoutes />} key={"admin"} />
        {/* Sub-Admin Routes - protected by authentication */}
        <Route path="/subadmin/*" element={<SubAdminRoutes />} key={"subadmin"} />
        {/* Trainer Routes - protected by authentication */}
        <Route path="/trainer/*" element={<TrainerRoutes />} key={"trainer"} />
        <Route path="/auth-callback" element={<AuthCallback />} key={"auth-callback"}/>
        <Route path="/setup-account" element={<AccountSetup />} key={"setup-account"}/>
        <Route path="/nutrition-blog" element={<NutritionBlog/>} key={"nutrition-blog"}/>
        <Route path="/subscription" element={<Subscription/>} key={"subscription"}/>
        <Route path="/payment-success" element={<PaymentSuccess />} key={"payment-success"}/>
        <Route path="/blogs-list" element={<BlogsList/>} key={"blogs-list"}/>
        <Route path="/stripe-cookie-help" element={<StripeCookieHelp />} key={"stripe-cookie-help"}/>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    
    {/* ChatBot toggle available on all pages */}
    <CookieBanner />
    <ConditionalChatBot />
    <PageViewTracker />
  </>
);

export default App;